import {
  coverImageUrl,
  orderedImages,
  PRODUCT_IMAGE_MAIN,
  type LocalizedProduct,
  type LocalizedProductCategory,
  type LocalizedText,
  type LocalizedModifierGroup,
  type MerchantProduct,
  type ProductModerationState,
  type MerchantModerationState,
  type ModifierGroup,
  type ProductCategory,
  type ProductStatus,
} from '@noor/eats-api';
import type {
  LocalizedField,
  MenuCategory,
  MenuModifierGroup,
  MenuProduct,
  ProductAvailability,
  ProductModeration,
} from '../model/schema';

// Mappers from the merchant API's wire shapes to the Menu screen's view model.
// Keeping them here (rather than in `api/`) makes the translation pure and
// unit-testable, and keeps protobuf-flavoured payloads out of the widgets.

/**
 * A category row for the sidebar.
 *
 * `moderation_state` is the category's merchant-facing review state. The menu
 * UI keeps the same local vocabulary as products (`pending`, `approved`, ...),
 * so protobuf enum values are translated here at the boundary.
 */
export function toMenuCategory(category: ProductCategory): MenuCategory {
  const moderation = toCategoryModeration(category.moderation_state);

  return {
    id: category.id,
    name: category.name,
    ...(category.sort_order === undefined ? {} : { sortOrder: category.sort_order }),
    ...(category.created_at ? { createdAt: category.created_at } : {}),
    ...(moderation === undefined ? {} : { moderation }),
    ...(category.rejection_reason?.trim()
      ? { rejectionReason: category.rejection_reason.trim() }
      : {}),
  };
}

function toCategoryModeration(
  state: MerchantModerationState | undefined,
): ProductModeration | undefined {
  if (state === 'MERCHANT_MODERATION_STATE_DRAFT') return 'draft';
  if (state === 'MERCHANT_MODERATION_STATE_ON_MODERATION') return 'pending';
  if (state === 'MERCHANT_MODERATION_STATE_APPROVED') return 'approved';
  if (state === 'MERCHANT_MODERATION_STATE_REJECTED') return 'rejected';
  return undefined;
}

/**
 * A category row built from a `LocalizedProductCategory` — what the
 * create/update RPCs answer with. The display `name` resolves to `ru`, matching
 * both the backend's own fallback for an untranslated locale and
 * {@link toMenuProductFromLocalized}.
 */
export function toMenuCategoryFromLocalized(
  category: LocalizedProductCategory,
): MenuCategory {
  const nameTranslations = toLocalizedField(category.name);

  return {
    ...toMenuCategory({ ...category, name: category.name.ru }),
    // Carried through so the rename form can re-seed all three inputs from the
    // server's own record — the read endpoints never supply them.
    ...(nameTranslations ? { nameTranslations } : {}),
  };
}

/**
 * A product card for the catalog.
 *
 * `effective_price` is the price after any applied promotion and `price` is the
 * list price, so a discount is present exactly when the two differ. The
 * percentage is derived rather than read from the promotion, because the
 * product payload carries only the promotion's *id*, and the catalog needs a
 * badge without a second request.
 */
export function toMenuProduct(product: MerchantProduct): MenuProduct {
  const listPrice = product.price;
  const effective = product.effective_price > 0 ? product.effective_price : product.price;
  const isDiscounted = effective < listPrice;
  const measurementType = product.measurement_type;
  const cover = coverImageUrl(product.images);
  const gallery = orderedImages(product.images);
  // Which gallery entry is the cover. `image_type` and `position` are
  // independent on the wire, so this is read from the type rather than assumed
  // to be the first entry; with no entry typed `main` the backend's own
  // fallback is the first photo, which is what `coverImageUrl` renders.
  const mainIndex = gallery.findIndex((image) => image.image_type === PRODUCT_IMAGE_MAIN);
  const coverIndex = mainIndex === -1 ? 0 : mainIndex;
  const moderation = toProductModeration(product.moderation_state);

  return {
    id: product.id,
    categoryId: product.product_category_id,
    name: product.name,
    ...(product.description ? { description: product.description } : {}),
    ...(product.nutrition ? { nutrition: product.nutrition } : {}),
    price: effective,
    ...(product.stock_quantity > 0 ? { stockQuantity: product.stock_quantity } : {}),
    ...(isDiscounted
      ? {
          originalPrice: listPrice,
          discountPercent: discountPercent(listPrice, effective),
        }
      : {}),
    // As of API v1.00.141 `images` is a gallery of image OBJECTS (id, url,
    // position, image_type) rather than bare URLs. The cover is the entry typed
    // `main`; the legacy top-level `image` string still wins when set, and an
    // unmarked gallery falls back to its first photo — same precedence the
    // string era had.
    ...(cover ? { imageUrl: cover } : {}),
    ...(gallery.length > 0
      ? {
          imageUrls: gallery.map((image) => image.url),
          // Positionally aligned with `imageUrls` — the editor pairs them to
          // tell the API which stored photos survived an edit.
          imageIds: gallery.map((image) => image.id),
          // Which stored photo is the COVER, stated rather than inferred.
          // `position` and `image_type` are independent on the wire: a merchant
          // can star the third photo without reordering the gallery, so the
          // first entry by position is not necessarily the cover. Seeding the
          // editor from position alone showed the wrong cover AND re-asserted
          // it on save, silently moving the merchant's choice.
          coverIndex,
        }
      : {}),
    availability: toProductAvailability(product.status),
    // Moderation state, kept separate from `availability`: `status` is the
    // branch's stop-list switch and `moderation_state` is the review outcome.
    // An unknown/blank value stays undefined so the card badges nothing rather
    // than asserting a state the backend never sent.
    ...(moderation === undefined ? {} : { moderation }),
    ...(product.rejection_reason?.trim()
      ? { rejectionReason: product.rejection_reason.trim() }
      : {}),
    ...(measurementType === 'MEASUREMENT_TYPE_UNSPECIFIED' ? {} : { measurementType }),
    // OFD fiscalization codes (v1.00.155). Empty strings are the wire's "not
    // set" — protobuf-JSON omits zero values — so they collapse to absent keys
    // rather than surfacing as blank-but-present values in the editor.
    ...(product.ikpu_code ? { ikpuCode: product.ikpu_code } : {}),
    hasMarkedCode: product.has_marked_code,
    ...(product.marked_code ? { markedCode: product.marked_code } : {}),
    ...(product.unit_code ? { unitCode: product.unit_code } : {}),
    ...(product.vat_percent == null ? {} : { vatPercent: product.vat_percent }),
    ...(product.measure_amount ? { measureAmount: product.measure_amount } : {}),
    // Selling hours: empty string is the wire's "not set" (sold all day / the
    // branch inherits the dish's), so it collapses to an absent key.
    ...(product.available_from ? { availableFrom: product.available_from } : {}),
    ...(product.available_to ? { availableTo: product.available_to } : {}),
    ...(product.own_available_from
      ? { ownAvailableFrom: product.own_available_from }
      : {}),
    ...(product.own_available_to ? { ownAvailableTo: product.own_available_to } : {}),
    alwaysReady: product.is_always_ready,
    isAdult: product.is_adult,
    modifiersDisabled: product.modifiers_disabled,
    // An always-ready product has no meaningful cook time, and the wire sends 0
    // both for "not set" and "no wait" — so it collapses to undefined rather
    // than a misleading "0 min" in the editor.
    ...(product.is_always_ready || product.cook_time_seconds <= 0
      ? {}
      : { cookTimeSeconds: product.cook_time_seconds }),
    // Always an array (never undefined) on a READ: the product either has
    // groups or genuinely has none. Undefined is reserved for the WRITE side,
    // where it means "don't touch" — see `MenuProduct.modifiers`.
    modifiers: product.modifiers.map(toMenuModifierGroup),
  };
}

/**
 * `v1ModifierGroup` → the editor's view model.
 *
 * This is the LOCALE-RESOLVED shape, so each name is a single string; it is
 * lifted into the view model's `LocalizedField` under `ru`, the backend's own
 * fallback locale. A merchant editing such a group sees Russian filled and the
 * other two blank — accurate, since the resolved read genuinely does not carry
 * them. `toMenuModifierGroupFromLocalized` is the lossless path and is what the
 * editor's by-id read uses.
 */
function toMenuModifierGroup(group: ModifierGroup): MenuModifierGroup {
  return {
    // A stored group has a server id; it is what an edit must send back so the
    // backend updates that group in place instead of creating a duplicate.
    ...(group.id ? { id: group.id } : {}),
    name: { ru: group.name },
    ...toSelectionBounds(group),
    options: group.options.map((option) => ({
      ...(option.id ? { id: option.id } : {}),
      name: { ru: option.name },
      priceDelta: option.price,
      ...(option.ikpu_code ? { ikpuCode: option.ikpu_code } : {}),
      ...(option.unit_code ? { unitCode: option.unit_code } : {}),
      ...(option.vat_percent == null ? {} : { vatPercent: option.vat_percent }),
    })),
  };
}

/**
 * The wire's selection pair → the view model's. `max_select` arrives as `0`
 * for "no ceiling" (protobuf-JSON omits zero values), which the view model
 * says as ABSENT — a positive number is a real cap, `1` being the old SINGLE.
 */
function toSelectionBounds(
  group: Pick<ModifierGroup, 'min_select' | 'max_select'>,
): Pick<MenuModifierGroup, 'minSelect' | 'maxSelect'> {
  return {
    minSelect: group.min_select,
    ...(group.max_select > 0 ? { maxSelect: group.max_select } : {}),
  };
}

/**
 * `v1LocalizedModifierGroup` → the view model, keeping every translation.
 *
 * As of v1.00.184 this is the shape of `LocalizedProduct.modifiers` itself
 * (v1.00.180 briefly carried it in a separate `localized_modifiers` field).
 * Blank `uz`/`en` members are dropped rather than carried as empty strings, matching
 * {@link toLocalizedField}: the wire sends `''` for "not translated yet", and
 * an empty string round-tripped as a real value would overwrite a translation
 * with nothing.
 */
export function toMenuModifierGroupFromLocalized(
  group: LocalizedModifierGroup,
): MenuModifierGroup {
  return {
    ...(group.id ? { id: group.id } : {}),
    name: toLocalizedField(group.name) ?? { ru: '' },
    ...toSelectionBounds(group),
    options: group.options.map((option) => ({
      ...(option.id ? { id: option.id } : {}),
      name: toLocalizedField(option.name) ?? { ru: '' },
      priceDelta: option.price,
      ...(option.ikpu_code ? { ikpuCode: option.ikpu_code } : {}),
      ...(option.unit_code ? { unitCode: option.unit_code } : {}),
      ...(option.vat_percent == null ? {} : { vatPercent: option.vat_percent }),
    })),
  };
}

/**
 * A product card built from a `LocalizedProduct` — what the create/update RPCs
 * answer with. Identical to {@link toMenuProduct} except that every translation
 * is carried through, so the editor can be re-seeded from the server's own
 * record instead of from what was submitted.
 *
 * The card's display `name` resolves to `ru`, matching the backend's own
 * fallback for a locale with no translation.
 */
export function toMenuProductFromLocalized(
  product: LocalizedProduct,
  branchIds?: string[],
): MenuProduct {
  const nameTranslations = toLocalizedField(product.name);
  const descriptionTranslations = toLocalizedField(product.description);
  const compositionTranslations = product.composition ?? undefined;

  return {
    // `modifiers` is localized on THIS shape (all three languages per name, as
    // of v1.00.184 — the side-by-side `localized_modifiers` field is gone), so
    // it cannot ride through `toMenuProduct`, whose input carries the resolved
    // single-string groups. It is emptied here and mapped losslessly below.
    ...toMenuProduct({
      ...product,
      name: firstTranslation(product.name),
      description: firstTranslation(product.description),
      composition: product.composition
        ? firstTranslation(product.composition)
        : undefined,
      modifiers: [],
    }),
    ...(nameTranslations ? { nameTranslations } : {}),
    ...(descriptionTranslations ? { descriptionTranslations } : {}),
    ...(compositionTranslations ? { compositionTranslations } : {}),
    ...(branchIds === undefined ? {} : { branchIds }),
    modifiers: product.modifiers.map(toMenuModifierGroupFromLocalized),
  };
}

/**
 * `v1LocalizedText` → the view model's translations, dropping the empty
 * members. The wire sends `''` for "not translated yet" (protobuf omits the
 * key at its zero value, and the translations table refuses to store an empty
 * string), so a blank must not surface as a real, empty translation. When
 * `ru` is blank, the first available translation anchors the view-model field
 * so the other languages still reach the editor.
 */
export function toLocalizedField(text: LocalizedText): LocalizedField | undefined {
  const ru = text.ru || text.uz || text.en;
  if (!ru) return undefined;
  return {
    ru,
    ...(text.uz ? { uz: text.uz } : {}),
    ...(text.en ? { en: text.en } : {}),
  };
}

function firstTranslation(text: LocalizedText): string {
  return text.ru || text.uz || text.en || '';
}

/**
 * `v1ProductStatus` → the catalog's sale state. Both the unset and the
 * `*_UNSPECIFIED` zero value mean "the branch never set one", which the
 * backend treats as sellable — so they map to `available` rather than
 * hiding the product behind a stop-list badge it never earned.
 */
export function toProductAvailability(
  status: ProductStatus | undefined,
): ProductAvailability {
  return status === 'PRODUCT_STATUS_STOP_LIST' ? 'stopList' : 'available';
}

/**
 * `moderation_state` → the catalog's moderation state.
 *
 * The wire types this as a free-form STRING, not an enum, so the accepted
 * spellings are matched leniently: the spec documents the bare
 * `created`/`ready_for_life`/`life` vocabulary, while the sibling restaurant
 * enum uses the `*_LIFECYCLE_STATUS_*` protobuf spelling — so both are read,
 * along with the `rejected` outcome the spec's prose omits.
 *
 * An unrecognised or empty value yields `undefined` rather than a default:
 * every state here is something the merchant is SHOWN, and guessing one would
 * badge a product with a review outcome it never received.
 */
export function toProductModeration(
  moderationState: ProductModerationState | undefined,
): ProductModeration | undefined {
  if (!moderationState) return undefined;
  // Both the `PRODUCT_` and `MERCHANT_` prefixes are emitted by the backend.
  const state = moderationState.replace(/^(PRODUCT|MERCHANT)_MODERATION_STATE_/, '');
  if (state === 'DRAFT') return 'draft';
  if (state === 'ON_MODERATION') return 'pending';
  if (state === 'APPROVED') return 'approved';
  if (state === 'REJECTED') return 'rejected';
  return undefined;
}

/**
 * Whole-percent discount between a list and an effective price, clamped to
 * 0–100. Rounded so a 1_000_000 → 900_000 cut reads as a clean "10%".
 */
function discountPercent(listPrice: number, effective: number): number {
  if (listPrice <= 0) return 0;
  const percent = Math.round(((listPrice - effective) / listPrice) * 100);
  return Math.min(100, Math.max(0, percent));
}
